import * as React from "react";
import {QuarterFilterItems} from "../../utils/dateUtil";
import {ALL_TYPES, ALL_YEARS, DocumentType, getDocumentType, MarketOutlook} from "../../utils/marketOutlookUtils";
import {getYears} from "./MarketOutlookUtils";
import {
    IMarketOutlookFilterSelections,
    MarketOutlookFilterByOptions,
} from "./MarketOutlook.page";
import {FormattedMessage} from "react-intl";
import {getLocale, getTranslation} from "../../utils/translationUtil";
import {NewSelectComponent} from "../common/NewSelect.component";
import {ISelectValue} from "../common/Select.component";

// Translation helpers for dropdown labels
function getTranslatedYearLabel(value: string): string {
    if (value === ALL_YEARS) {
        return getTranslation("market-outlooks.filter-all-years", ALL_YEARS);
    }
    return value;
}

function getTranslatedQuarterLabel(value: string): string {
    switch (value) {
        case QuarterFilterItems.ALL.toString():
            return getTranslation("market-outlooks.filter-all-quarters", QuarterFilterItems.ALL);
        case QuarterFilterItems.Q1.toString():
            return getTranslation("market-outlooks.filter-q1", QuarterFilterItems.Q1);
        case QuarterFilterItems.Q2.toString():
            return getTranslation("market-outlooks.filter-q2", QuarterFilterItems.Q2);
        case QuarterFilterItems.Q3.toString():
            return getTranslation("market-outlooks.filter-q3", QuarterFilterItems.Q3);
        case QuarterFilterItems.Q4.toString():
            return getTranslation("market-outlooks.filter-q4", QuarterFilterItems.Q4);
        default:
            return value;
    }
}

function getTranslatedTypeLabel(value: string): string {
    switch (value) {
        case ALL_TYPES:
            return getTranslation("market-outlooks.filter-all-types", ALL_TYPES);
        case DocumentType.CMA.toString():
            return getTranslation("market-outlooks.filter-type-cma", DocumentType.CMA);
        case DocumentType.MTV.toString():
            return getTranslation("market-outlooks.filter-type-mtv", DocumentType.MTV);
        case DocumentType.QIO.toString():
            return getTranslation("market-outlooks.filter-type-qio", DocumentType.QIO);
        case DocumentType.MB.toString():
            return getTranslation("market-outlooks.filter-type-mb", DocumentType.MB);
        default:
            return value;
    }
}

export interface IMarketOutlooksFilterPropsFromParent {
    documents: MarketOutlook[];
    handleFilterChange: (event: any, id: MarketOutlookFilterByOptions) => void;
    handleClearFilters: () => void;
    selections: IMarketOutlookFilterSelections;
}

export const MarketOutlooksFilter: React.FunctionComponent<IMarketOutlooksFilterPropsFromParent> = (props) => {
    function getYearsSelectValues(): ISelectValue[] {
        const yearsItems = getYears(props.documents);
        return [ALL_YEARS, ...yearsItems].map((year) => ({
            id: year,
            name: getTranslatedYearLabel(year),
        }));
    }

    function getQuarterSelectValues(): ISelectValue[] {
        return Object.values(QuarterFilterItems).map((quarter) => ({
            id: quarter,
            name: getTranslatedQuarterLabel(quarter),
        }));
    }

    function getDocumentTypeSelectValues(): ISelectValue[] {
        const types = props.documents
            .map((it) => getDocumentType(it))
            .filter((elem, index, self) => index === self.indexOf(elem))
            .sort((item1, item2) => item1.toLowerCase().localeCompare(item2.toLowerCase()));

        return [ALL_TYPES, ...types].map((type) => ({
            id: type,
            name: getTranslatedTypeLabel(type),
        }));
    }

    function handleYearChange(e: any) {
        props.handleFilterChange(e, MarketOutlookFilterByOptions.BY_YEAR);
    }

    function handleQuarterChange(e: any) {
        props.handleFilterChange(e, MarketOutlookFilterByOptions.BY_QUARTER);
    }

    function handleTypeChange(e: any) {
        props.handleFilterChange(e, MarketOutlookFilterByOptions.BY_TYPE);
    }

    // Dropdown widths sized to fit the longest option text per language
    const isFrench = (getLocale() || "").startsWith("fr");
    const yearWidth = isFrench ? 190 : 130;
    const quarterWidth = isFrench ? 195 : 150;
    const typeWidth = isFrench ? 370 : 310;

    return (
        <div className="market-outlooks-filter" data-testid="market-outlooks-filter">
            <NewSelectComponent
                id="market-outlooks-filter-year"
                values={getYearsSelectValues()}
                selected={props.selections.yearSelection}
                width={yearWidth}
                handleChange={handleYearChange}
                classNameSuffix="market-outlooks-year"
            />

            <NewSelectComponent
                id="market-outlooks-filter-quarter"
                values={getQuarterSelectValues()}
                selected={props.selections.quarterSelection}
                width={quarterWidth}
                handleChange={handleQuarterChange}
                classNameSuffix="market-outlooks-quarter"
            />

            <NewSelectComponent
                id="market-outlooks-filter-type"
                values={getDocumentTypeSelectValues()}
                selected={props.selections.typeSelection}
                width={typeWidth}
                handleChange={handleTypeChange}
                classNameSuffix="market-outlooks-type"
            />

            <button
                className="market-outlooks-filter__clear"
                onClick={props.handleClearFilters}
                type="button"
            >
                <FormattedMessage id="filters.clear-filters" defaultMessage="Clear Filters" />
            </button>
        </div>
    );
};

